import { environment } from '../environments/environment';

interface ApiEndpoint {
  path: string;
  payload?: Record<string, unknown>;
  payloadOverridesByEntity?: Record<string, Record<string, unknown>>;
}

export interface AddInConfig {
  name: string;
  entityFilter?: { hiddenAttributesPath?: string; hiddenAttributesByEntity?: Record<string, string[]> };
  api: { baseUrl: string; uploadBatchSize: number;
    upload: ApiEndpoint & { workflowName: string; workflowNamesByEntity?: Record<string, string> };
    entityDefinitions: ApiEndpoint & { listFilter?: { enabled: boolean; key: string; value: string | number | boolean | null } };
    referenceValues: ApiEndpoint;
    entityPermissions?: ApiEndpoint;
    download: ApiEndpoint & { filters?: Array<{ attributeName: string; operator: string; logicalOperator: string; beginScope: string; endScope: string; attributeValue: unknown }> };
  };
  auth: { url: string; realm: string; clientId: string };
  text: {
    prefix: string; brand: string; heading: string; entityLabel: string;
    description: string; uploadButton: string; ready: string; preparing: string;
    pleaseWait: string; operationFailed: string; retry: string;
  };
  theme: Record<string, string>;
}

export let addInConfig: AddInConfig;

async function readJson(path: string): Promise<any> {
  const response = await fetch(new URL(path, document.baseURI).href, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Cannot load ${path} (${response.status}).`);
  return response.json();
}

export async function loadConfig(): Promise<void> {
  const config = await readJson(environment.configPath);
  if (config.entityFilter !== undefined) {
    const filter = config.entityFilter;
    if (!filter || typeof filter !== 'object' || Array.isArray(filter)) {
      throw new Error('Invalid entityFilter configuration.');
    }
    if (filter.hiddenAttributesPath !== undefined) {
      if (typeof filter.hiddenAttributesPath !== 'string' || !/^assets\/.+\.json$/.test(filter.hiddenAttributesPath) ||
          filter.hiddenAttributesPath.split('/').includes('..')) {
        throw new Error('entityFilter.hiddenAttributesPath must be an assets/ JSON path.');
      }
      const conditions = await readJson(filter.hiddenAttributesPath);
      if (!conditions || typeof conditions !== 'object' || Array.isArray(conditions) || conditions.hiddenAttributesByEntity === undefined) {
        throw new Error('Filter conditions require hiddenAttributesByEntity.');
      }
      filter.hiddenAttributesByEntity = conditions.hiddenAttributesByEntity;
    }
    const hidden = filter.hiddenAttributesByEntity;
    if (hidden !== undefined && (!hidden || typeof hidden !== 'object' || Array.isArray(hidden) ||
        Object.entries(hidden).some(([entity, names]) => !entity.trim() || !Array.isArray(names) ||
          names.some(name => typeof name !== 'string' || !name.trim())))) {
      throw new Error('entityFilter.hiddenAttributesByEntity must map entity names to arrays of attribute names.');
    }
  }
  const api = config.api;
  const auth = { url: environment.keycloakUrl, realm: environment.realm, clientId: environment.clientId };
  if (typeof config?.name !== 'string' || !config.name.trim() ||
      typeof environment.base_url !== 'string' || !/^https?:\/\//.test(environment.base_url) ||
      ['upload', 'entityDefinitions', 'download', 'referenceValues'].some(key =>
        typeof api?.[key]?.path !== 'string' || !/^\/(?!\/)/.test(api[key].path)) ||
      (api?.entityPermissions !== undefined &&
        (typeof api.entityPermissions?.path !== 'string' || !/^\/(?!\/)/.test(api.entityPermissions.path))) ||
      typeof api?.upload?.workflowName !== 'string' || !api.upload.workflowName.trim() ||
      typeof auth?.url !== 'string' || !/^https?:\/\//.test(auth.url) ||
      [auth.realm, auth.clientId].some(value => typeof value !== 'string' || !value.trim()) ||
      !Number.isSafeInteger(environment.uploadBatchSize) || environment.uploadBatchSize < 1) {
    throw new Error('Invalid add-in name or API configuration.');
  }
  const labels = ['prefix', 'brand', 'heading', 'entityLabel', 'description', 'uploadButton', 'ready', 'preparing', 'pleaseWait', 'operationFailed', 'retry'];
  const listFilter = api.entityDefinitions.listFilter;
  if (listFilter !== undefined && (!listFilter || typeof listFilter !== 'object' || Array.isArray(listFilter) ||
      typeof listFilter.enabled !== 'boolean' || typeof listFilter.key !== 'string' || !listFilter.key.trim() ||
      !Object.prototype.hasOwnProperty.call(listFilter, 'value') ||
      (listFilter.value !== null && !['string', 'number', 'boolean'].includes(typeof listFilter.value)))) {
    throw new Error('api.entityDefinitions.listFilter requires enabled, a non-empty key, and a scalar value.');
  }
  for (const key of ['download', 'upload', 'referenceValues', 'entityDefinitions', 'entityPermissions']) {
    if (api[key] === undefined) continue;
    const payload = api[key].payload;
    if (payload !== undefined && (!payload || typeof payload !== 'object' || Array.isArray(payload))) {
      throw new Error(`Invalid api.${key}.payload configuration.`);
    }
    const overrides = api[key].payloadOverridesByEntity;
    if (overrides !== undefined && (!overrides || typeof overrides !== 'object' || Array.isArray(overrides) ||
        Object.entries(overrides).some(([entity, value]) => !entity.trim() || !value || typeof value !== 'object' || Array.isArray(value)))) {
      throw new Error(`Invalid api.${key}.payloadOverridesByEntity configuration.`);
    }
  }
  if (api.download.filters !== undefined && (!Array.isArray(api.download.filters) || api.download.filters.some((filter: any) =>
      !filter || ['attributeName', 'operator', 'logicalOperator', 'beginScope', 'endScope'].some(key => typeof filter[key] !== 'string') ||
      !filter.attributeName.trim() || !filter.operator.trim() || !Object.prototype.hasOwnProperty.call(filter, 'attributeValue')))) throw new Error('Invalid api.download.filters.');
  if (api.upload.workflowNamesByEntity !== undefined &&
      (!api.upload.workflowNamesByEntity || typeof api.upload.workflowNamesByEntity !== 'object' || Array.isArray(api.upload.workflowNamesByEntity) ||
       Object.entries(api.upload.workflowNamesByEntity).some(([entity, workflow]) =>
         !entity.trim() || typeof workflow !== 'string' || !workflow.trim()))) {
    throw new Error('api.upload.workflowNamesByEntity must map entity names to non-empty workflow names.');
  }
  if (!config.text || labels.some(key => typeof config.text[key] !== 'string')) throw new Error('Missing or invalid text configuration.');
  if (!config.theme || typeof config.theme !== 'object' || Array.isArray(config.theme) ||
      Object.entries(config.theme).some(([key, value]) => !/^--[a-zA-Z0-9-]+$/.test(key) || typeof value !== 'string')) {
    throw new Error('Theme must contain CSS variable names and string values.');
  }
  // Endpoint paths come from JSON; server URLs and Keycloak settings come from the environment.
  addInConfig = { ...config, api: { ...api, baseUrl: environment.base_url, uploadBatchSize: environment.uploadBatchSize }, auth };
  document.title = config.name;
  for (const [key, value] of Object.entries(addInConfig.theme)) document.documentElement.style.setProperty(key, value);
}
